import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/modules/auth/useAuth';
import { getSupabase } from '@/lib/supabase';
import type { HottrBlockRow } from '@/lib/database.types';

/**
 * Block list — the user's own set of blocked auth IDs.
 *
 * Source of truth is `hottr_blocks` (RLS scoped to the blocker). The hook
 * exposes a Set for O(1) filter checks plus block/unblock mutators that
 * keep the local set in sync optimistically.
 */
export interface BlocksState {
  /** Auth user IDs the current user has blocked. */
  blockedIds: Set<string>;
  loading: boolean;
  /** Block a user. Returns ok or a renderable error. */
  block(targetUserId: string, reason?: string): Promise<{ ok: boolean; error?: string }>;
  /** Remove an existing block. */
  unblock(targetUserId: string): Promise<{ ok: boolean; error?: string }>;
  isBlocked(targetUserId: string): boolean;
}

export const useBlocks = (): BlocksState => {
  const { user } = useAuth();
  const [blockedIds, setBlockedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setBlockedIds(new Set());
      setLoading(false);
      return;
    }
    const supabase = getSupabase();
    if (!supabase) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('hottr_blocks')
        .select('blocked_id')
        .eq('blocker_id', user.id);
      if (cancelled) return;
      if (error || !data) {
        setBlockedIds(new Set());
        setLoading(false);
        return;
      }
      setBlockedIds(new Set((data as Pick<HottrBlockRow, 'blocked_id'>[]).map((r) => r.blocked_id)));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const block = useCallback(
    async (targetUserId: string, reason?: string) => {
      if (!user) return { ok: false, error: 'Not logged in.' };
      if (targetUserId === user.id) return { ok: false, error: 'You cannot block yourself.' };
      const supabase = getSupabase();
      if (!supabase) return { ok: false, error: 'No connection.' };

      // Optimistic add
      setBlockedIds((prev) => {
        const next = new Set(prev);
        next.add(targetUserId);
        return next;
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const db = supabase as any;
      const { error } = (await db.from('hottr_blocks').insert({
        blocker_id: user.id,
        blocked_id: targetUserId,
        reason: reason ?? null,
      })) as { error: { message: string } | null };
      if (error && !/duplicate key|unique/i.test(error.message)) {
        // Rollback if it wasn't already-blocked
        setBlockedIds((prev) => {
          const next = new Set(prev);
          next.delete(targetUserId);
          return next;
        });
        return { ok: false, error: error.message };
      }
      return { ok: true };
    },
    [user?.id],
  );

  const unblock = useCallback(
    async (targetUserId: string) => {
      if (!user) return { ok: false, error: 'Not logged in.' };
      const supabase = getSupabase();
      if (!supabase) return { ok: false, error: 'No connection.' };

      setBlockedIds((prev) => {
        const next = new Set(prev);
        next.delete(targetUserId);
        return next;
      });

      const { error } = await supabase
        .from('hottr_blocks')
        .delete()
        .eq('blocker_id', user.id)
        .eq('blocked_id', targetUserId);
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    },
    [user?.id],
  );

  const isBlocked = useMemo(
    () => (id: string) => blockedIds.has(id),
    [blockedIds],
  );

  return { blockedIds, loading, block, unblock, isBlocked };
};
